import { Suit } from '../types';

export const GAME_CONSTANTS = {
  // Game rules
  TARGET_SCORE: 16,
  CARDS_PER_PLAYER: 3,
  TABLE_CARDS: 4,
  SBARAZZO_DECLARE_THRESHOLD: 8, // somma mano ≤ 8 per dichiarare sbarazzo

  // Points
  SBARAZZO_POINTS: 3,
  THREE_ACES_POINTS: 6,
  MOST_CARDS_BONUS: 1,
  MOST_COINS_BONUS: 1,
  SETTEBELLO_BONUS: 1,
  PRIMIERA_BONUS: 1,
  DIECI_DENARI_BONUS: 1,
  DUE_DENARI_PENALTY: -5,
  ZERO_COINS_PENALTY: -10,

  // AI
  AI_MINIMAX_DEPTH: 4,
  CPU_DELAY_MS: 1500,

  // Multiplayer
  DISCONNECT_TIMEOUT_MS: 60000,
  MATCH_CODE_DIGITS: 6,
  MATCH_TTL_24H: 24 * 60 * 60 * 1000,
  MATCH_TTL_48H: 48 * 60 * 60 * 1000,
  POLLING_INTERVAL_MS: 1000,
  RETRY_INTERVAL_MS: 500,
} as const;

export const SUIT_IMG_MAP: Record<Suit, string> = {
  [Suit.COINS]: 'denari',
  [Suit.CUPS]: 'coppe',
  [Suit.SWORDS]: 'spade',
  [Suit.CLUBS]: 'bastoni',
};

// === PREMIUM FEATURES ===
// NOTE: VITE_PREMIUM_ENABLED is set in GitHub Actions secrets and defaults to true.
// For local dev, set VITE_PREMIUM_ENABLED=true in your .env file.
export const PREMIUM_ENABLED = import.meta.env.VITE_PREMIUM_ENABLED !== 'false';
// Defaults to true. Only 'false' disables premium features.

// Coin reward values
export const COIN_REWARDS = {
  WIN_MATCH: 15,
  STREAK_BONUS_3: 20,
  STREAK_BONUS_5: 50,
  DAILY_CHALLENGE: 25,
  TOURNAMENT_PARTICIPATION: 10,
  TOURNAMENT_TOP_4: 30,
  TOURNAMENT_WIN: 100,
} as const;

// XP reward values
export const XP_REWARDS = {
  WIN_MATCH: 30,
  LOSS_MATCH: 10,
} as const;

// Level-up coin rewards
export const LEVEL_UP_COINS: Record<number, number> = {
  2: 10,
  3: 15,
  4: 20,
  5: 25,
  6: 30,
  7: 35,
  8: 40,
  9: 45,
  10: 50,
  11: 55,
  12: 60,
  13: 65,
  14: 70,
  15: 75,
  16: 80,
  17: 85,
  18: 90,
  19: 95,
  20: 100,
  21: 110,
  22: 120,
  23: 130,
  24: 140,
  25: 150,
  26: 160,
  27: 170,
  28: 180,
  29: 190,
  30: 200,
};

// Achievement coin rewards
export const ACHIEVEMENT_COINS = {
  first_win: 5,
  streak_3: 15,
  streak_5: 30,
  streak_10: 75,
  cards_100: 10,
  scopa_10: 20,
  sbarazzo_5: 25,
  primiera_10: 20,
  tournament_win: 50,
  level_25: 40,
  level_50: 100,
} as const;

// Achievement definitions (Phase 2)
export const ACHIEVEMENTS = [
  { id: 'first_win',     name: 'Prima vittoria',     description: 'Vinci la tua prima partita',         conditionType: 'wins',       conditionValue: 1,  coinReward: ACHIEVEMENT_COINS.first_win    },
  { id: 'streak_3',      name: 'In fuga',            description: 'Ottieni 3 vittorie consecutive',    conditionType: 'streak',      conditionValue: 3,  coinReward: ACHIEVEMENT_COINS.streak_3     },
  { id: 'streak_5',      name: 'Inarrestabile',       description: 'Ottieni 5 vittorie consecutive',    conditionType: 'streak',      conditionValue: 5,  coinReward: ACHIEVEMENT_COINS.streak_5     },
  { id: 'streak_10',     name: 'leggenda',            description: 'Ottieni 10 vittorie consecutive',   conditionType: 'streak',      conditionValue: 10, coinReward: ACHIEVEMENT_COINS.streak_10    },
  { id: 'cards_100',     name: 'Collezionista',       description: 'Cattura 100 carte in totale',        conditionType: 'cards_captured', conditionValue: 100, coinReward: ACHIEVEMENT_COINS.cards_100  },
  { id: 'scopa_10',      name: 'Spazza',              description: 'Fai 10 scopa in totale',            conditionType: 'scopa',       conditionValue: 10, coinReward: ACHIEVEMENT_COINS.scopa_10   },
  { id: 'sbarazzo_5',    name: 'Sbarazzino',          description: 'Dichiara sbarazzo 5 volte',         conditionType: 'sbarazzo',    conditionValue: 5,  coinReward: ACHIEVEMENT_COINS.sbarazzo_5  },
  { id: 'primiera_10',   name: 'Primiera',            description: 'Vinci la primiera 10 volte',         conditionType: 'primiera',    conditionValue: 10, coinReward: ACHIEVEMENT_COINS.primiera_10 },
  { id: 'tournament_win',name: 'Campione',            description: 'Vinci un torneo',                    conditionType: 'tournament_win', conditionValue: 1,  coinReward: ACHIEVEMENT_COINS.tournament_win },
  { id: 'level_25',      name: 'Veteran',             description: 'Raggiungi il livello 25',            conditionType: 'level',       conditionValue: 25, coinReward: ACHIEVEMENT_COINS.level_25   },
  { id: 'level_50',      name: 'Maestro',             description: 'Raggiungi il livello 50',            conditionType: 'level',       conditionValue: 50, coinReward: ACHIEVEMENT_COINS.level_50   },
] as const;

// Coin shop items (Phase 1 — card backs)
export const CARD_BACKS = {
  cb_classic: { name: 'Classico', price: 0, rarity: 'default', level: 0 },
  cb_modern: { name: 'Minimal', price: 0, rarity: 'common', level: 3 },
  cb_vintage: { name: 'Vintage', price: 200, rarity: 'common', level: 0 },
  cb_gold: { name: 'Oro', price: 500, rarity: 'rare', level: 0 },
  cb_neon: { name: 'Neon', price: 800, rarity: 'rare', level: 0 },
  cb_dragon: { name: 'Drago', price: 1200, rarity: 'legendary', level: 0 },
} as const;

// Daily challenges pool
export const DAILY_CHALLENGES = [
  { id: 'win_3', name: 'Vinci 3 partite', description: 'Vinci 3 partite in qualsiasi modalità', target: 3, coinReward: 25 },
  { id: 'score_20', name: 'Re dei punti', description: 'Segna 20 punti in una partita', target: 20, coinReward: 30 },
  { id: 'scopa_2', name: 'Spazza!', description: 'Fai 2 scopa in una partita', target: 2, coinReward: 25 },
  { id: 'capture_10', name: 'Cattura 10', description: 'Cattura 10 carte in una partita', target: 10, coinReward: 20 },
  { id: 'primiera_win', name: 'Vinci la primiera', description: 'Vinci la primiera in una partita', target: 1, coinReward: 20 },
] as const;
